/**
 * Real-time Subscription Hook
 * Provides Supabase real-time updates with React Query integration
 *
 * This hook subscribes to database changes and automatically invalidates
 * the relevant React Query cache, triggering refetches.
 */

import { useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "../services/supabase";

// Type for postgres changes payload (compatible with Supabase realtime)
type PostgresChangePayload<T> = {
  eventType: "INSERT" | "UPDATE" | "DELETE";
  new: T | Record<string, unknown>;
  old: T | Record<string, unknown>;
  schema: string;
  table: string;
  commit_timestamp: string;
  errors: string[] | null;
};

type RealtimeEvent = "INSERT" | "UPDATE" | "DELETE" | "*";

interface UseRealtimeSubscriptionOptions<T extends Record<string, unknown>> {
  /** Table name to subscribe to */
  table: string;
  /** Schema name (defaults to 'public') */
  schema?: string;
  /** Events to listen for (defaults to all) */
  events?: RealtimeEvent[];
  /** Query keys to invalidate on change */
  queryKeys: readonly unknown[][];
  /** Optional filter column (e.g., 'organization_id') */
  filterColumn?: string;
  /** Optional filter value */
  filterValue?: string;
  /** Whether the subscription is enabled (defaults to true) */
  enabled?: boolean;
  /** Optional callback when a change occurs */
  onInsert?: (payload: T) => void;
  onUpdate?: (payload: { old: T; new: T }) => void;
  onDelete?: (payload: T) => void;
  /** Debounce time in ms to batch rapid changes (defaults to 100ms) */
  debounceMs?: number;
}

/**
 * Hook for subscribing to real-time database changes
 * Automatically invalidates React Query cache when changes occur
 *
 * @example
 * // Basic usage - invalidate vouchers queries on any change
 * useRealtimeSubscription({
 *   table: 'vouchers',
 *   queryKeys: [['vouchers'], ['redemptions']],
 * });
 *
 * @example
 * // With filter - only listen to specific organization's changes
 * useRealtimeSubscription({
 *   table: 'vouchers',
 *   filterColumn: 'organization_id',
 *   filterValue: orgId,
 *   queryKeys: [['vouchers', 'org', orgId]],
 * });
 *
 * @example
 * // With callbacks - handle specific events
 * useRealtimeSubscription({
 *   table: 'vouchers',
 *   queryKeys: [['vouchers']],
 *   onInsert: (voucher) => console.log('New voucher:', voucher),
 *   onUpdate: ({ old, new: newData }) => console.log('Updated:', old, '->', newData),
 * });
 */
const isRealtimeDebugEnabled = String(import.meta.env.VITE_REALTIME_DEBUG || "")
  .toLowerCase()
  .trim();

const shouldLogRealtimeDebug =
  isRealtimeDebugEnabled === "true" || isRealtimeDebugEnabled === "1";

const debugLog = (...args: unknown[]) => {
  if (shouldLogRealtimeDebug) {
    console.debug("[Realtime][debug]", ...args);
  }
};

const attachNativeSocketDiagnostics = (
  channel: RealtimeChannel,
  channelName: string,
  getSubscribedAt: () => number | null,
): (() => void) | null => {
  if (!shouldLogRealtimeDebug || typeof window === "undefined") {
    return null;
  }

  const realtimeSocket = channel.socket as typeof channel.socket & {
    conn?: WebSocket | null;
    connectionState?: () => string;
  };

  if (!realtimeSocket) {
    debugLog(`${channelName} no realtime socket available for diagnostics`);
    return null;
  }

  let cleanup: (() => void) | null = null;
  let pollTimer: number | null = null;

  const registerListeners = (nativeSocket: WebSocket) => {
    const handleClose = (event: CloseEvent) => {
      console.warn(`[Realtime][debug] ${channelName} websocket closed`, {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      });
      const subscribedAt = getSubscribedAt();
      if (subscribedAt) {
        const elapsed = Date.now() - subscribedAt;
        console.warn(`[Realtime][debug] ${channelName} lifetime: ${elapsed}ms`);
      }
    };

    const handleError = (event: Event) => {
      console.error(`[Realtime][debug] ${channelName} websocket error`, event);
    };

    nativeSocket.addEventListener("close", handleClose);
    nativeSocket.addEventListener("error", handleError);

    cleanup = () => {
      nativeSocket.removeEventListener("close", handleClose);
      nativeSocket.removeEventListener("error", handleError);
    };
  };

  const tryAttach = () => {
    const nativeSocket = realtimeSocket.conn;
    if (nativeSocket && typeof nativeSocket.addEventListener === "function") {
      registerListeners(nativeSocket as WebSocket);
      return;
    }

    const state = realtimeSocket.connectionState?.();
    if (state === "closed" || state === "closing") {
      debugLog(`${channelName} socket ${state} before diagnostics attached`);
      return;
    }

    pollTimer = window.setTimeout(tryAttach, 50);
  };

  tryAttach();

  return () => {
    if (pollTimer) {
      clearTimeout(pollTimer);
    }
    if (cleanup) {
      cleanup();
    }
  };
};

// Stable default to avoid creating new array references on each render
const DEFAULT_EVENTS: RealtimeEvent[] = ["*"];

export function useRealtimeSubscription<T extends Record<string, unknown>>({
  table,
  schema = "public",
  events,
  queryKeys,
  filterColumn,
  filterValue,
  enabled = true,
  onInsert,
  onUpdate,
  onDelete,
  debounceMs = 100,
}: UseRealtimeSubscriptionOptions<T>) {
  const queryClient = useQueryClient();
  const channelRef = useRef<RealtimeChannel | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingInvalidations = useRef<Set<string>>(new Set());
  // Use ref to track if we've already attempted subscription for this table
  // Key includes table params so different tables can have independent attempts
  const subscriptionKeyRef = useRef<string>("");
  const subscriptionFailedRef = useRef(false);
  const subscribedAtRef = useRef<number | null>(null);
  const socketCleanupRef = useRef<(() => void) | null>(null);

  // Use stable default for events to avoid dependency changes
  const stableEvents = events || DEFAULT_EVENTS;
  // Stringify events for stable dependency comparison
  const eventsKey = JSON.stringify(stableEvents);

  // Store callbacks in refs to prevent dependency changes
  const callbacksRef = useRef({ onInsert, onUpdate, onDelete });
  callbacksRef.current = { onInsert, onUpdate, onDelete };

  // Debounced invalidation to batch rapid changes
  const scheduleInvalidation = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      // Invalidate all pending query keys
      queryKeys.forEach((queryKey) => {
        queryClient.invalidateQueries({
          queryKey: queryKey as readonly unknown[],
        });
      });
      pendingInvalidations.current.clear();
      debounceTimerRef.current = null;
    }, debounceMs);
  }, [queryClient, queryKeys, debounceMs]);

  // Store scheduleInvalidation in a ref to avoid dependency issues
  const scheduleInvalidationRef = useRef(scheduleInvalidation);
  scheduleInvalidationRef.current = scheduleInvalidation;

  useEffect(() => {
    // Don't subscribe if disabled
    if (!enabled) {
      return;
    }

    // Generate unique subscription key based on table params
    const subscriptionKey = `${schema}:${table}:${filterColumn || ""}:${filterValue || ""}`;

    // Check if we already tried and failed for this exact subscription
    if (
      subscriptionFailedRef.current &&
      subscriptionKeyRef.current === subscriptionKey
    ) {
      return;
    }

    // Update the key we're attempting
    subscriptionKeyRef.current = subscriptionKey;

    // Generate unique channel name
    const channelName = `realtime:${schema}:${table}${filterColumn ? `:${filterColumn}=${filterValue}` : ""}`;

    // Add a small delay before subscribing to avoid rate limiting on page load
    const subscriptionDelay = setTimeout(() => {
      console.log(`[Realtime] Subscribing to ${channelName}`);

      // Build the filter configuration
      const filterConfig: {
        event: RealtimeEvent;
        schema: string;
        table: string;
        filter?: string;
      } = {
        event: stableEvents.length === 1 ? stableEvents[0] : "*",
        schema,
        table,
      };

      // Add filter if specified
      if (filterColumn && filterValue) {
        filterConfig.filter = `${filterColumn}=eq.${filterValue}`;
      }

      // Create channel and subscribe
      const channel = supabase
        .channel(channelName)
        .on(
          "postgres_changes" as const,
          filterConfig,
          (payload: PostgresChangePayload<Record<string, unknown>>) => {
            console.log(
              `[Realtime] ${table} ${(payload as PostgresChangePayload<T>).eventType}:`,
              payload,
            );

            // Call specific callbacks if provided (using refs to avoid dependency)
            const {
              onInsert: insert,
              onUpdate: update,
              onDelete: del,
            } = callbacksRef.current;
            const typedPayload = payload as PostgresChangePayload<T>;
            switch (typedPayload.eventType) {
              case "INSERT":
                insert?.(typedPayload.new as T);
                break;
              case "UPDATE":
                update?.({
                  old: typedPayload.old as T,
                  new: typedPayload.new as T,
                });
                break;
              case "DELETE":
                del?.(typedPayload.old as T);
                break;
            }

            // Schedule cache invalidation
            scheduleInvalidationRef.current();
          },
        );

      if (socketCleanupRef.current) {
        socketCleanupRef.current();
        socketCleanupRef.current = null;
      }
      socketCleanupRef.current = attachNativeSocketDiagnostics(
        channel,
        channelName,
        () => subscribedAtRef.current,
      );

      channel.subscribe((status, err) => {
        const connectionState = channel.socket?.connectionState?.();
        debugLog(
          `${channelName} status: ${status}`,
          connectionState ? `(socket: ${connectionState})` : "",
          err ? { message: err.message } : "",
        );

        if (status === "SUBSCRIBED") {
          subscribedAtRef.current = Date.now();
          console.log(`[Realtime] ${channelName} connected`);
        } else if (status === "CHANNEL_ERROR") {
          console.error(`[Realtime] Error subscribing to ${channelName}`, err);
          subscriptionFailedRef.current = true;
        } else if (status === "CLOSED" || status === "TIMED_OUT") {
          const subscribedAt = subscribedAtRef.current;
          const elapsed = subscribedAt
            ? `${Date.now() - subscribedAt}ms`
            : "unknown duration";
          // Mark as failed using ref (no state update = no re-render = no retry)
          console.warn(
            `[Realtime] ${channelName} subscription ${status} after ${elapsed} - realtime may not be enabled for this table`,
          );
          if (err) {
            console.error(
              `[Realtime] ${channelName} close reason:`,
              err.message,
            );
          }
          subscriptionFailedRef.current = true;
        }
      });

      channelRef.current = channel;
    }, 1000); // 1 second delay to avoid rate limiting

    // Cleanup on unmount only - don't reset failed ref so we don't retry
    return () => {
      clearTimeout(subscriptionDelay);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }

      if (socketCleanupRef.current) {
        socketCleanupRef.current();
        socketCleanupRef.current = null;
      }
    };
  }, [enabled, table, schema, eventsKey, filterColumn, filterValue]);

  // Return channel status and manual controls
  return {
    /** Manually trigger cache invalidation */
    invalidate: () => {
      queryKeys.forEach((queryKey) => {
        queryClient.invalidateQueries({
          queryKey: queryKey as readonly unknown[],
        });
      });
    },
    /** Current channel reference */
    channel: channelRef.current,
  };
}

/**
 * Convenience hook for subscribing to multiple tables at once
 */
export function useMultiTableRealtimeSubscription(
  subscriptions: UseRealtimeSubscriptionOptions<Record<string, unknown>>[],
) {
  const queryClient = useQueryClient();
  const channelsRef = useRef<RealtimeChannel[]>([]);

  useEffect(() => {
    // Subscribe to all tables
    subscriptions.forEach((sub) => {
      if (!sub.enabled) {
        return;
      }

      const channelName = `realtime:${sub.schema || "public"}:${sub.table}`;

      const channel = supabase
        .channel(channelName)
        .on(
          "postgres_changes" as const,
          {
            event: "*",
            schema: sub.schema || "public",
            table: sub.table,
          },
          () => {
            sub.queryKeys.forEach((queryKey) => {
              queryClient.invalidateQueries({
                queryKey: queryKey as readonly unknown[],
              });
            });
          },
        )
        .subscribe();

      channelsRef.current.push(channel);
    });

    // Cleanup all channels
    return () => {
      channelsRef.current.forEach((channel) => {
        supabase.removeChannel(channel);
      });
      channelsRef.current = [];
    };
  }, [subscriptions, queryClient]);
}

export default useRealtimeSubscription;
